import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const STATUS_STYLE = {
  Proposed: "border-info/50 bg-info-soft text-info",
  Confirmed: "border-success/50 bg-success-soft text-success",
  Rescheduled: "border-warning/50 bg-warning-soft text-warning",
  Completed: "border-primary/50 bg-primary-soft text-primary",
  "Feedback Received": "border-success/50 bg-success-soft text-success",
};
const DOT = {
  Proposed: "bg-info", Confirmed: "bg-success", Rescheduled: "bg-warning",
  Completed: "bg-primary", "Feedback Received": "bg-success",
};

const initials = (names = []) => names
  .map((n) => n.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join(""))
  .join(" ").toUpperCase();

const gridFor = (year, month) => {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(1 - ((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

export const DemoCalendar = ({ onOpen }) => {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const days = useMemo(() => gridFor(cursor.y, cursor.m), [cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demos/calendar", {
        params: { date_from: iso(days[0]), date_to: iso(days[41]) },
      });
      setRows(data);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const monthLabel = new Date(cursor.y, cursor.m, 1)
    .toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const clashing = rows.filter((r) => r.clash_with.length);

  const forDay = (key) => rows
    .filter((r) => (r.travel_start || r.start) <= key && key <= (r.travel_end || r.end || r.start))
    .map((r) => ({ ...r, session: r.start <= key && key <= (r.end || r.start) }));

  return (
    <div className="space-y-4" data-testid="demo-calendar">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button size="iconSm" variant="outline" data-testid="cal-prev"
            onClick={() => setCursor(({ y, m }) => m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 })}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="iconSm" variant="outline" data-testid="cal-next"
            onClick={() => setCursor(({ y, m }) => m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 })}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <p className="font-display text-lg font-bold" data-testid="cal-month">{monthLabel}</p>
        <Button size="sm" variant="ghost" data-testid="cal-today"
          onClick={() => setCursor({ y: today.getFullYear(), m: today.getMonth() })}>
          Today
        </Button>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        <div className="ml-auto flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-info" />Proposed</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-success" />Confirmed</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-warning" />Rescheduled</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary" />Completed</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded border border-dashed border-muted-foreground/60" />travel day
          </span>
        </div>
      </div>

      {clashing.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-warning/40 bg-warning-soft/50 px-3 py-2.5"
          data-testid="cal-clash-banner">
          <TriangleAlert className="h-4 w-4 text-warning" />
          <span className="text-sm font-semibold text-warning">
            {clashing.length} session(s) overlap for the same person:
          </span>
          <span className="num text-xs text-muted-foreground">
            {clashing.map((c) => `${c.number} (${c.clash_people.join(", ")})`).join(" \u00b7 ")}
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
        <div className="grid grid-cols-7 border-b border-border bg-muted/50">
          {DOW.map((d) => (
            <div key={d} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const key = iso(d);
            const entries = forDay(key);
            const outside = d.getMonth() !== cursor.m;
            return (
              <div key={key} data-testid={`cal-day-${key}`}
                className={cn("min-h-[104px] border-b border-r border-border/70 p-1.5 last:border-r-0",
                  outside && "bg-muted/25", key === iso(today) && "bg-primary-soft/30")}>
                <p className={cn("num mb-1 px-0.5 text-[11px] font-semibold",
                  outside ? "text-muted-foreground/50" : "text-muted-foreground",
                  key === iso(today) && "text-primary")}>
                  {d.getDate()}
                </p>
                <div className="space-y-1">
                  {entries.map((e) => (
                    <button key={e.id} type="button" onClick={() => onOpen?.(e.id)}
                      data-testid={`cal-entry-${e.number}-${key}`}
                      title={`${e.number} \u00b7 ${e.kind} \u00b7 ${e.customer_name}${e.session ? "" : " (travel day)"}${e.clash_with.length ? ` \u00b7 clashes with ${e.clash_with.join(", ")}` : ""}`}
                      className={cn("w-full rounded-md border px-1.5 py-1 text-left transition-transform hover:-translate-y-px",
                        STATUS_STYLE[e.status] || "border-border bg-muted text-foreground",
                        !e.session && "border-dashed bg-transparent opacity-80",
                        e.clash_with.length && "ring-1 ring-warning")}>
                      <span className="flex items-center gap-1">
                        <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", DOT[e.status] || "bg-muted-foreground")} />
                        <span className="truncate text-[11px] font-semibold leading-tight">{e.customer_name}</span>
                      </span>
                      <span className="num block truncate text-[10px] leading-tight opacity-80">
                        {initials(e.assigned_to_names) || e.kind}{e.session ? "" : " \u00b7 travel"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
